import React, { useState } from 'react';
import { 
  Download, 
  Loader2, 
  Search, 
  Calendar as CalendarIcon,
  FileText
} from 'lucide-react';
import { BookingBasicInfo } from '../../types';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

export const BookingCsvPage: React.FC = () => {
  const [month, setMonth] = useState('');
  const [bookings, setBookings] = useState<BookingBasicInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  // Convert month from YYYY-MM format to MMM format for API (e.g., "2025-10" -> "Oct")
  const formatMonthForApi = (monthInput: string): string => {
    if (!monthInput || monthInput.trim() === '') {
      return '';
    }
    
    try {
      const [year, month] = monthInput.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString('en-US', {
        month: 'short'
      });
    } catch {
      return monthInput;
    }
  };

  // Format month for display (e.g., "2025-10" -> "Oct 2025")
  const formatMonthForDisplay = (monthInput: string): string => {
    if (!monthInput || monthInput.trim() === '') {
      return '';
    }
    
    try {
      const [year, month] = monthInput.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return monthInput;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!month || month.trim() === '') {
      toast.error('Please enter a month');
      return;
    }

    setIsLoading(true);
    setIsFetched(false);
    
    try {
      // Convert month format from YYYY-MM to MMM YYYY for API call
      const formattedMonth = formatMonthForApi(month);
      const data = await apiService.getBookingsCsv(formattedMonth);
      setBookings(data);
      setIsFetched(true);
      toast.success(`Loaded ${data.length} bookings`);
    } catch (error: any) {
      console.error('Failed to load bookings CSV:', error);
      toast.error(error.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (bookings.length === 0) {
      toast.error('No data to download');
      return;
    }

    // Define CSV headers based on BookingBasicInfo structure
    const headers = [
      'ID',
      'Trip Name',
      'Duration',
      'Name of Traveller',
      'Mobile Number',
      'Email',
      'Payment Date',
      'Trip Date',
      'Per Person',
      'Number of People',
      'Amount Paid',
      'Paid'
    ];

    // Convert data to CSV rows
    const csvRows = bookings.map(booking => {
      return [
        booking.id || '',
        booking.tripName || '',
        booking.duration || '',
        booking.nameOfTraveller || '',
        booking.mobileNumber || '',
        booking.email || '',
        booking.paymentDate || '',
        booking.tripDate || '',
        (booking.perPerson !== undefined && booking.perPerson !== null) ? booking.perPerson.toString() : '0',
        (booking.numberOfPeople !== undefined && booking.numberOfPeople !== null) ? booking.numberOfPeople.toString() : '0',
        (booking.amountPaid !== undefined && booking.amountPaid !== null) ? booking.amountPaid.toString() : '0',
        booking.paid || ''
      ].map(field => {
        // Escape quotes and wrap in quotes if contains comma or newline
        const fieldStr = String(field);
        if (fieldStr.includes(',') || fieldStr.includes('\n') || fieldStr.includes('"')) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const formattedMonthForFilename = month ? formatMonthForApi(month).replace(' ', '_') : 'all';
    link.setAttribute('download', `bookings_${formattedMonthForFilename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('CSV file downloaded successfully');
  };

  const formatPrice = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '₹0';
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (paid: string) => {
    if (!paid) return 'text-gray-700 bg-gray-100';
    const paidLower = paid.toLowerCase();
    if (paidLower === 'online') return 'text-green-700 bg-green-100';
    if (paidLower === 'offline' || paidLower === 'cash') return 'text-blue-700 bg-blue-100';
    return 'text-gray-700 bg-gray-100';
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Booking CSV</h1>
              <p className="text-sm text-gray-500">Export booking data for a specific month</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Month Input Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-2">
                Month <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="month"
                    id="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Select month (e.g., 2025-01)"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      <span>Load Data</span>
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Format: YYYY-MM (e.g., 2025-01 for January 2025)
              </p>
            </div>
          </form>
        </div>

        {/* Results Table */}
        {isFetched && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Table Header with Download Button */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Booking Data</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'} found for {formatMonthForDisplay(month)}
                </p>
              </div>
              <button
                onClick={handleDownloadCsv}
                disabled={bookings.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download CSV</span>
              </button>
            </div>

            {/* Table */}
            {bookings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Traveller
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trip Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trip Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Per Person
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        People
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount Paid
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paid
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {booking.nameOfTraveller || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.mobileNumber || 'N/A'}
                          </div>
                          {booking.email && (
                            <div className="text-xs text-gray-500">
                              {booking.email}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {booking.tripName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {booking.duration || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {booking.paymentDate || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {booking.tripDate || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatPrice(booking.perPerson)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {booking.numberOfPeople || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatPrice(booking.amountPaid)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.paid)}`}>
                            {booking.paid || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No bookings found for this month</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

